import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { ChevronDown, ChevronUp, Trash2 } from 'react-feather';
import { DeletePolicy, SelectPolicyRenewalLoaded } from 'actions';
import ConfirmAction from 'components/confirm-action';
import DocViewer from 'components/doc-viewer';
import { Button, ToggleButton } from 'components/quashed';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import { WORKPLACE_EDIT_ROUTE, DASHBOARD_ROUTE } from 'constants/routes';
import {
  convertToDateOnlyString,
  getActionHeaderAndContent,
  getUpcomingRenewal,
  prettifyNumber,
  trackPolicyDetailsDelete,
  trackPolicyDetailsEdit,
} from 'helpers';
import history from 'helpers/history';
import usePolicyDetails from '../usePolicyDetails';
import { selectPolicyScans } from 'selectors/policyScanSelectors';
import { IAttachment, IEmployer, IPolicy, IPolicyRenewal } from 'types';
import styles from '../styles/PolicyDetailsForm.module.scss';
import FormFields from './FormFields';
import { AttachmentItem } from './PolicyDetailsDocs';
import {
  getDefaultPolicyDetailsValues,
  getFieldByPolicyType,
  getLifeInsuredFromForm,
  getValueByType,
} from '../utils';

interface IProps {
  attachments?: IAttachment[];
  className?: string;
  employer?: IEmployer;
  errorClassName?: string;
  errorMsg?: string;
  editing?: boolean;
  showButtons?: boolean;
  isOwner?: boolean;
  mini?: boolean;
  policy: IPolicy;
  renewals?: IPolicyRenewal[];
  title?: string;
  validateOnMount?: boolean;
  onCancel?(): void;
  onSubmit?(data: any): void;
  updatePolicy(p: IPolicy, submitted?: boolean): void;
}

const getOptionName = (options = [], id) => options.find(R.propEq('value', id));

const PolicyDetailsMini = ({ policy, fields, setExpand }) => {
  const isLife = policy.policyType === INSURANCE_TYPES_NAMES.LIFE;
  const insuredField = getFieldByPolicyType(policy.policyType)[0];
  const value = getValueByType(insuredField, policy[insuredField.key]);

  return (
    <div className={styles.mini} onClick={() => setExpand(true)}>
      {insuredField && (
        <div className={styles.miniItem}>
          <label>{insuredField.label}</label>
          <span>{isLife ? value.join(', ') : value}</span>
        </div>
      )}
      {policy.amount && (
        <div className={styles.miniItem}>
          <label>Insured amount</label>
          <span>${prettifyNumber(policy.amount)}</span>
        </div>
      )}
      <div className={styles.miniItem}>
        <label>Renewal date</label>
        <span>{convertToDateOnlyString(policy.renewalDate, 'DD/MM/YYYY')}</span>
      </div>
      <div className={styles.seeMore}>
        <span>See more</span> <ChevronDown size={20} />
      </div>
    </div>
  );
};

const PolicyDetailsForm = ({
  attachments = [],
  children,
  className,
  employer,
  errorClassName,
  errorMsg,
  editing = false,
  showButtons = true,
  isOwner,
  mini,
  policy,
  renewals,
  title = 'Details',
  validateOnMount,
  onCancel = R.F,
  onSubmit,
  updatePolicy,
}: PropsWithChildren<IProps>) => {
  const dispatch = useDispatch();
  const [pdfSrc, setPdfSrc] = useState(null);
  const [isEdit, setEdit] = useState(editing);
  const [openConfirm, setConfirm] = useState(false);
  const [expand, setExpand] = useState(!mini);
  const policyScans = useSelector(selectPolicyScans);
  const { header, content } = getActionHeaderAndContent(policyScans, policy.id);

  const defaultValues = useMemo(
    () => getDefaultPolicyDetailsValues(policy),
    [policy],
  );

  const form = useForm({ defaultValues });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    trigger,
    watch,
    setValue,
  } = form;

  useEffect(() => {
    if (validateOnMount) {
      trigger();
    }
  }, []);

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues]);

  const formValues = watch();
  const { fields, loading, options } = usePolicyDetails(formValues);

  useEffect(() => {
    const numberPlateValue = formValues.numberPlate;
    if (numberPlateValue) {
      setValue('numberPlate', numberPlateValue.toUpperCase());
    }
  }, [setValue, formValues.numberPlate]);

  const handleEdit = useCallback(() => {
    const upcomingRenewal = getUpcomingRenewal(renewals);
    if (policy.policyRenewalAdded && upcomingRenewal) {
      dispatch(SelectPolicyRenewalLoaded(upcomingRenewal));
      return;
    }

    trackPolicyDetailsEdit(policy);
    if (employer) {
      history.push(WORKPLACE_EDIT_ROUTE);
    } else {
      setEdit(true);
    }
  }, [employer, policy, renewals]);

  const handleConfirmCancel = useCallback(() => setConfirm(false), []);

  const handleConfirm = useCallback(() => {
    history.push(DASHBOARD_ROUTE);
    trackPolicyDetailsDelete(policy);
    dispatch(DeletePolicy(policy.id));
  }, [policy]);

  const handleConfirmOpen = useCallback((e) => {
    e.preventDefault();
    setConfirm(true);
  }, []);

  const submit = (data) => {
    const policyProvider = getOptionName(
      options.policyProviderId,
      data.policyProviderId,
    );
    const policySubType = getOptionName(
      options.policySubTypeId,
      data.policySubTypeId,
    );

    const toUpdate = {
      ...data,
      policyProviderName: policyProvider?.text,
      policySubTypeName: policySubType?.text,
    };

    if (data.policyType === INSURANCE_TYPES_NAMES.LIFE) {
      toUpdate.insured = getLifeInsuredFromForm(data);
    }

    onSubmit(toUpdate);

    if (!editing) {
      setEdit(false);
    }
  };

  const handleReset = () => {
    reset();
    setEdit(false);
    onCancel();
  };

  const handleViewClick = ({ downloadUrl }) => setPdfSrc(downloadUrl);

  const handlePdfViewerClose = () => setPdfSrc(null);

  const handleConfirmPremium = () => {
    updatePolicy(
      {
        ...policy,
        premiumConfirmed: true,
      },
      false,
    );
  };

  if (!expand) {
    return (
      <PolicyDetailsMini
        fields={fields}
        policy={formValues}
        setExpand={setExpand}
      />
    );
  }

  return (
    <form
      className={classnames(styles.container, className)}
      onSubmit={handleSubmit(submit)}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <h3>{title}</h3>
            {showButtons && isOwner && (
              <ToggleButton
                id="PolicyDetails_EditBtn"
                className={styles.editBtn}
                buttonText={
                  isEdit ? (
                    'Editing...'
                  ) : (
                    <u>{`Edit ${
                      policy.policyRenewalAdded ? 'renewal' : 'now'
                    }`}</u>
                  )
                }
                onClick={handleEdit}
                toggled={false}
              />
            )}
          </div>
          {mini && <ChevronUp size={20} onClick={() => setExpand(false)} />}
        </div>
        <div className={styles.body}>
          <div className={styles.fieldsContainer}>
            <FormFields
              control={control}
              disabled={!isEdit}
              errors={errors}
              fields={fields[0]}
              isOwner={isOwner}
              loading={loading}
              values={formValues}
              onConfirm={handleConfirmPremium}
            />
            <FormFields
              control={control}
              disabled={!isEdit}
              errors={errors}
              fields={fields[1]}
              isOwner={isOwner}
              loading={loading}
              values={formValues}
              onConfirm={handleConfirmPremium}
            />
          </div>

          {attachments.length > 0 && (
            <div className={styles.documentsContainer}>
              <label>Uploaded documents</label>
              <div className={styles.documents}>
                {attachments.map((att) => (
                  <AttachmentItem
                    key={att.id}
                    attachment={att}
                    onClick={handleViewClick}
                    onDelete={null}
                  />
                ))}
              </div>
            </div>
          )}

          {showButtons && isEdit && (
            <div className={styles.buttons}>
              <Trash2 className={styles.delete} onClick={handleConfirmOpen} />
              <Button
                id="PolicyDetails_CancelBtn"
                className={styles.cancel}
                inverted={true}
                text="Don’t save"
                type="button"
                onClick={handleReset}
              />
              <Button
                id="PolicyDetails_SaveBtn"
                className={styles.submit}
                type="submit"
                text="Save"
              />
            </div>
          )}
        </div>

        <ConfirmAction
          open={openConfirm}
          header={header}
          content={content}
          cancelText="Back"
          confirmText="Delete"
          onCancel={handleConfirmCancel}
          onConfirm={handleConfirm}
        />
        <DocViewer src={pdfSrc} onClose={handlePdfViewerClose} />
      </div>

      {!R.isEmpty(errors) && errorMsg && (
        <div className={classnames(styles.error, errorClassName)}>
          {errorMsg}
        </div>
      )}
      {children}
    </form>
  );
};

export default PolicyDetailsForm;
